import axios from 'axios'
import React, { useEffect, useState, useCallback } from 'react'
import { Dropdown, Modal, Spinner, Table, Badge, Form } from 'react-bootstrap'
import Pagination from 'react-bootstrap/Pagination'
import { useHistory } from 'react-router-dom'
import Select from 'react-select'
import plus from '../../assets/icons/plus.svg'
import threedot from '../../assets/icons/threedot.svg'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSearch } from '@fortawesome/free-solid-svg-icons'
import {
  GetAllVendorsEnd,
  EditVendorEnd,
  DeleteVendorEnd,
  GetAdminProfileUrl,
  VendorAllScreensEnd,
} from '../../constants/api.constants'
import Toast from '../../utils/Toast/Toast'
import { debounce } from 'lodash'
import { SEARCH_REGEX } from '../../constants/regex'
import NewVendorModal from '../../components/Modals/NewVendorModal/NewVendorModal'
import './VendorManagement.scss'

const VendorManagement = () => {
  const [show, setShow] = useState(false)
  const handleClose = () => setShow(false)
  const handleShow = () => setShow(true)

  let history = useHistory()
  const [allVendors, setAllVendors] = useState([])
  const [searchKey, setSearchKey] = useState('')
  const [page, setPage] = useState(1)
  const [spinner, setSpinner] = useState(true)
  const [totalDoc, setTotalDoc] = useState(0)
  const [userData, setUserData] = useState({})
  const [statusFilter, setStatusFilter] = useState('')

  // Edit modal states
  const [editModal, setEditModal] = useState(false)
  const [editData, setEditData] = useState({})
  const [editSpinner, setEditSpinner] = useState(false)

  // Delete modal states
  const [deleteModal, setDeleteModal] = useState(false)
  const [deleteData, setDeleteData] = useState({})
  const [deleteSpinner, setDeleteSpinner] = useState(false)

  // Screen assignment states
  const [allScreens, setAllScreens] = useState([])
  const [assignScreenModal, setAssignScreenModal] = useState(false)
  const [selectedVendor, setSelectedVendor] = useState(null)
  const [selectedScreens, setSelectedScreens] = useState([]) // react-select format: [{value, label}]
  const [assignSpinner, setAssignSpinner] = useState(false)

  useEffect(() => {
    GetUser()
    loadAllScreens()
  }, [])

  useEffect(() => {
    if (userData?._id) {
      loadAllVendors()
    }
  }, [page, statusFilter, userData])

  const loadAllVendors = async (tempSearch) => {
    setSpinner(true)
    let url = GetAllVendorsEnd + `?page=${page}`

    if (tempSearch?.length > 0) {
      url += `&filter=${tempSearch}`
    } else if (searchKey.length > 0) {
      url += `&filter=${searchKey}`
    }

    if (statusFilter) {
      url += `&status=${statusFilter}`
    }

    try {
      const response = await axios.get(url, {
        headers: {
          menuboard: localStorage.getItem('menu_token'),
        },
      })

      if (response.status === 200) {
        setAllVendors(response?.data?.data || [])
        setTotalDoc(response?.data?.total_document || 0)
        setPage(response?.data?.page || 1)
        setSpinner(false)
      } else {
        throw new Error(
          response?.data?.msg || 'Cannot load vendors data, try again later'
        )
      }
    } catch (error) {
      setSpinner(false)
      Toast(
        'err',
        error.response?.data?.msg || 'Cannot load vendors data, try again later'
      )
    }
  }

  const loadAllScreens = async () => {
    try {
      const response = await axios.get(VendorAllScreensEnd, {
        headers: {
          menuboard: localStorage.getItem('menu_token'),
        },
      })
      if (response.status === 200) {
        setAllScreens(response?.data?.data || [])
      }
    } catch (error) {
      console.error('Failed to load screens:', error)
    }
  }

  useEffect(() => {
    if (userData?.company_id) {
      history.push('/')
    }
  }, [userData, history])

  const GetUser = async () => {
    try {
      const response = await axios.get(GetAdminProfileUrl, {
        headers: {
          menuboard: localStorage.getItem('menu_token'),
        },
      })

      if (response.status === 200) {
        setUserData(response?.data?.data)
      } else throw new Error(response?.data?.msg)
    } catch (error) {
      Toast('err', error.response?.data?.msg)
    }
  }

  const debounceFn = useCallback(debounce(loadAllVendors, 500), [])

  const handleInputChange = (e) => {
    const sanitizedValue = e.target.value.replace(SEARCH_REGEX, '')
    setSearchKey(sanitizedValue)
    setPage(1)
    debounceFn(sanitizedValue)
  }

  const handleEditVendor = (vendor) => {
    setEditData({
      id: vendor._id,
      name: vendor.name,
      email: vendor.email,
      expiry_date: vendor.expiry_date ? vendor.expiry_date.split('T')[0] : '',
      is_active: vendor.is_active,
    })
    setEditModal(true)
  }

  const handleEditSubmit = async () => {
    if (!editData.name) {
      Toast('err', 'Please enter vendor name')
      return
    }
    if (!editData.email) {
      Toast('err', 'Please enter vendor email')
      return
    }

    setEditSpinner(true)
    try {
      const payload = {
        id: editData.id,
        name: editData.name,
        email: editData.email,
        expiry_date: editData.expiry_date || null,
        is_active: editData.is_active,
      }

      const response = await axios.put(EditVendorEnd, payload, {
        headers: {
          menuboard: localStorage.getItem('menu_token'),
        },
      })

      if (response.status === 200) {
        Toast('success', 'Vendor updated successfully!')
        setEditSpinner(false)
        setEditModal(false)
        loadAllVendors()
      } else {
        throw new Error(response?.data?.msg || 'Something went wrong!')
      }
    } catch (error) {
      setEditSpinner(false)
      Toast('err', error.response?.data?.msg || 'Something went wrong!')
    }
  }

  const handleDeleteVendor = (vendor) => {
    setDeleteData(vendor)
    setDeleteModal(true)
  }

  const handleDeleteConfirm = async () => {
    setDeleteSpinner(true)
    try {
      const response = await axios.delete(
        DeleteVendorEnd + `?id=${deleteData._id}`,
        {
          headers: {
            menuboard: localStorage.getItem('menu_token'),
          },
        }
      )

      if (response.status === 200) {
        Toast('success', 'Vendor deleted successfully!')
        setDeleteSpinner(false)
        setDeleteModal(false)
        loadAllVendors()
      } else {
        throw new Error(response?.data?.msg || 'Something went wrong!')
      }
    } catch (error) {
      setDeleteSpinner(false)
      Toast('err', error.response?.data?.msg || 'Something went wrong!')
    }
  }

  // Screen assignment handlers
  const handleAssignScreens = (vendor) => {
    setSelectedVendor(vendor)
    // Convert assigned screens to react-select format
    const currentScreens = vendor.assigned_screens?.map(s => {
      const screenId = s._id || s
      const screenData = allScreens.find(sc => sc._id === screenId)
      return screenData ? {
        value: screenData._id,
        label: `${screenData.screen_name} (${screenData.store_id?.name || 'N/A'})`
      } : null
    }).filter(Boolean) || []
    setSelectedScreens(currentScreens)
    setAssignScreenModal(true)
  }

  const handleScreenChange = (selectedOptions) => {
    setSelectedScreens(selectedOptions || [])
  }

  const handleAssignSubmit = async () => {
    setAssignSpinner(true)
    try {
      const payload = {
        id: selectedVendor._id,
        assigned_screens: selectedScreens.map(s => s.value),
      }

      const response = await axios.put(EditVendorEnd, payload, {
        headers: {
          menuboard: localStorage.getItem('menu_token'),
        },
      })

      if (response.status === 200) {
        Toast('success', 'Screens assigned successfully!')
        setAssignSpinner(false)
        setAssignScreenModal(false)
        loadAllVendors()
      } else {
        throw new Error(response?.data?.msg || 'Something went wrong!')
      }
    } catch (error) {
      setAssignSpinner(false)
      Toast('err', error.response?.data?.msg || 'Something went wrong!')
    }
  }

  // Convert allScreens to react-select options
  const screenOptions = allScreens.map(screen => ({
    value: screen._id,
    label: `${screen.screen_name} (${screen.store_id?.name || 'N/A'}) - ID: ${screen.unique_id}`
  }))

  const getVendorStatus = (vendor) => {
    if (!vendor.is_active) {
      return { status: 'Inactive', variant: 'secondary' }
    }
    if (vendor.expiry_date) {
      const expiryDate = new Date(vendor.expiry_date)
      const today = new Date()
      if (expiryDate < today) {
        return { status: 'Expired', variant: 'danger' }
      }
      const weekFromNow = new Date()
      weekFromNow.setDate(weekFromNow.getDate() + 7)
      if (expiryDate <= weekFromNow) {
        return { status: 'Expiring Soon', variant: 'warning' }
      }
    }
    return { status: 'Active', variant: 'success' }
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'Perpetual (No Expiry)'
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  // Pagination
  let items = []
  let totalPage = totalDoc < 10 ? 1 : Math.ceil(totalDoc / 10)
  for (let number = 1; number <= totalPage; number++) {
    items.push(
      <Pagination.Item key={number} active={number === page}>
        {number}
      </Pagination.Item>
    )
  }

  return (
    <div className='row py-3'>
      <div className='col-12'>
        <div className='d-flex justify-content-between align-items-center'>
          <h3 className='fw-bold'>Vendor Management</h3>
          <button
            className='primary-btn d-flex justify-content-center align-items-center'
            onClick={handleShow}
          >
            <img className='me-3' src={plus} alt='' /> New Vendor
          </button>
        </div>

        <div className='d-flex justify-content-between align-items-center mt-4'>
          <div className='custom-input me-2'>
            <label htmlFor=''>
              <b>Search Vendor</b>
            </label>
            <br />
            <div style={{ position: 'relative' }}>
              <input
                type='text'
                placeholder='Search by name, email, etc.'
                value={searchKey}
                onChange={handleInputChange}
              />
              <FontAwesomeIcon icon={faSearch} className='icon-container' />
            </div>
          </div>

          <div className='custom-dropdown ms-2'>
            <label htmlFor=''>Status</label>
            <select onChange={(e) => {
              setPage(1)
              setStatusFilter(e.target.value)
            }}>
              <option value=''>All</option>
              <option value='active'>Active</option>
              <option value='inactive'>Inactive</option>
              <option value='expired'>Expired</option>
            </select>
          </div>
        </div>

        {spinner && (
          <div className='d-flex justify-content-center align-items-center my-5'>
            <Spinner animation='border' style={{ color: '#558f55' }} />
          </div>
        )}

        {!spinner && allVendors.length > 0 ? (
          <Table
            striped
            bordered
            hover
            responsive
            borderless={true}
            className='my-5 text-start'
          >
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Expiry Date</th>
                <th>Assigned Screens</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {allVendors.map((vendor, idx) => {
                const statusInfo = getVendorStatus(vendor)
                return (
                  <tr key={idx}>
                    <td>{vendor.name}</td>
                    <td>{vendor.email}</td>
                    <td>{formatDate(vendor.expiry_date)}</td>
                    <td>
                      <Badge bg='info'>{vendor.assigned_screens?.length || 0} screens</Badge>
                    </td>
                    <td>
                      <Badge bg={statusInfo.variant}>{statusInfo.status}</Badge>
                    </td>
                    <td className='text-center'>
                      <Dropdown drop='start' style={{ cursor: 'pointer' }}>
                        <Dropdown.Toggle
                          variant='transparent'
                          id='dropdown-basic'
                        >
                          <img src={threedot} alt='' />
                        </Dropdown.Toggle>
                        <Dropdown.Menu className='mt-4'>
                          <Dropdown.Item onClick={() => handleEditVendor(vendor)}>
                            Edit Vendor
                          </Dropdown.Item>
                          <Dropdown.Item onClick={() => handleAssignScreens(vendor)}>
                            Assign Screens
                          </Dropdown.Item>
                          <Dropdown.Item onClick={() => handleDeleteVendor(vendor)}>
                            Delete Vendor
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </Table>
        ) : (
          !spinner && (
            <h3 className='text-secondary my-5 text-center'>No Vendors Found!</h3>
          )
        )}

        {totalDoc > 0 && !spinner && allVendors.length > 0 && (
          <div className='d-flex justify-content-center align-items-center my-5'>
            <Pagination>
              {items.map((item, index) => (
                <Pagination.Item
                  key={index}
                  active={index + 1 === page}
                  onClick={() => setPage(index + 1)}
                >
                  {index + 1}
                </Pagination.Item>
              ))}
            </Pagination>
          </div>
        )}
      </div>

      {/* New Vendor Modal */}
      <NewVendorModal
        show={show}
        handleClose={handleClose}
        loadAllVendors={loadAllVendors}
      />

      {/* Edit Vendor Modal */}
      <Modal show={editModal} onHide={() => setEditModal(false)} size='md'>
        <Modal.Header closeButton style={{ border: 'none' }}>
          <Modal.Title style={{ fontSize: '22px' }}>Edit Vendor</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className='mb-3'>
            <div className='plain-input my-3'>
              <label htmlFor=''>Vendor Name*</label>
              <br />
              <input
                type='text'
                placeholder='Enter vendor name'
                value={editData.name}
                onChange={(e) =>
                  setEditData({ ...editData, name: e.target.value })
                }
              />
            </div>

            <div className='plain-input my-3'>
              <label htmlFor=''>Email*</label>
              <br />
              <input
                type='email'
                placeholder='Enter email'
                value={editData.email}
                disabled
              />
            </div>

            <div className='plain-input my-3'>
              <label htmlFor=''>
                Access Expiry Date
                <small className='text-muted ms-2'>
                  (Leave empty for perpetual access)
                </small>
              </label>
              <br />
              <input
                type='date'
                value={editData.expiry_date}
                onChange={(e) =>
                  setEditData({ ...editData, expiry_date: e.target.value })
                }
              />
              {editData.expiry_date && (
                <button
                  className='btn btn-link btn-sm p-0 mt-1'
                  onClick={() => setEditData({ ...editData, expiry_date: '' })}
                >
                  Clear expiry (set to perpetual)
                </button>
              )}
            </div>

            <div className='plain-dropdown my-3'>
              <label htmlFor=''>Account Status</label>
              <select
                value={editData.is_active ? 'active' : 'inactive'}
                onChange={(e) =>
                  setEditData({
                    ...editData,
                    is_active: e.target.value === 'active',
                  })
                }
              >
                <option value='active'>Active</option>
                <option value='inactive'>Inactive (Manually Blocked)</option>
              </select>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button
            className='primary-btn-light'
            onClick={() => setEditModal(false)}
          >
            Cancel
          </button>
          <button
            className='primary-btn d-flex justify-content-center align-items-center'
            onClick={handleEditSubmit}
          >
            Update Vendor
            {editSpinner && (
              <Spinner className='ms-2' animation='border' size='sm' />
            )}
          </button>
        </Modal.Footer>
      </Modal>

      {/* Assign Screens Modal */}
      <Modal show={assignScreenModal} onHide={() => setAssignScreenModal(false)} size='lg'>
        <Modal.Header closeButton style={{ border: 'none' }}>
          <Modal.Title style={{ fontSize: '22px' }}>
            Assign Screens to {selectedVendor?.name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className='text-muted mb-3'>
            Search and select the screens this vendor should have access to:
          </p>
          <Select
            isMulti
            options={screenOptions}
            value={selectedScreens}
            onChange={handleScreenChange}
            placeholder="Search and select screens..."
            noOptionsMessage={() => "No screens found"}
            isClearable
            isSearchable
            closeMenuOnSelect={false}
            styles={{
              control: (base) => ({
                ...base,
                minHeight: '45px',
              }),
              multiValue: (base) => ({
                ...base,
                backgroundColor: '#e8f5e9',
              }),
              multiValueLabel: (base) => ({
                ...base,
                color: '#2e7d32',
              }),
              multiValueRemove: (base) => ({
                ...base,
                color: '#2e7d32',
                ':hover': {
                  backgroundColor: '#c8e6c9',
                  color: '#1b5e20',
                },
              }),
            }}
          />
          <div className='mt-3'>
            <small className='text-muted'>
              <strong>Tip:</strong> Start typing to search by screen name, store name, or screen ID
            </small>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className='me-auto'>
            <Badge bg='info'>{selectedScreens.length} screen(s) selected</Badge>
          </div>
          <button
            className='primary-btn-light'
            onClick={() => setAssignScreenModal(false)}
          >
            Cancel
          </button>
          <button
            className='primary-btn d-flex justify-content-center align-items-center'
            onClick={handleAssignSubmit}
          >
            Save Assignment
            {assignSpinner && (
              <Spinner className='ms-2' animation='border' size='sm' />
            )}
          </button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={deleteModal} onHide={() => setDeleteModal(false)} size='sm'>
        <Modal.Header closeButton style={{ border: 'none' }}>
          <Modal.Title style={{ fontSize: '18px' }}>Delete Vendor</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Are you sure you want to delete vendor{' '}
            <strong>{deleteData.name}</strong>?
          </p>
          <p className='text-danger small'>This action cannot be undone.</p>
        </Modal.Body>
        <Modal.Footer>
          <button
            className='primary-btn-light'
            onClick={() => setDeleteModal(false)}
          >
            Cancel
          </button>
          <button
            className='danger-btn d-flex justify-content-center align-items-center'
            onClick={handleDeleteConfirm}
          >
            Delete
            {deleteSpinner && (
              <Spinner className='ms-2' animation='border' size='sm' />
            )}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}

export default VendorManagement
